import React, { useCallback, useEffect, useRef, useState } from 'react';
import Children from '../../interfaces/children.interface';
import Gap from '../../interfaces/gap.interface';
import Layer from '../../interfaces/layer.interface';
import TestId from '../../interfaces/test-id.interface';

type ValidDropdownPositions = 'left' | 'center' | 'right';

export interface Props extends Children, Layer, Gap, TestId {
  /** The dropdown's position (defaults to `center`) */
  position?: ValidDropdownPositions;

  /** The element to use as a trigger */
  trigger: React.ReactNode;
}

/** The dropdown's position styles */
const pos = new Map([
  ['left', 'left-0'],
  ['center', 'left-1/2 -translate-x-1/2'],
  ['right', 'right-0'],
]);

/** A select like component to render a dropdown */
const Dropdown = ({
  children,
  gap = 'none',
  layer = 0,
  position = 'center',
  testId,
  trigger,
}: Props): React.ReactElement => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  /** Close the dropdown when clicked outside of the component */
  const close = useCallback(
    (evt: MouseEvent): void => {
      const { target }: { target: EventTarget | null } = evt;

      if (ref.current && (ref.current.contains(target as Node) || ref.current === target)) {
        return;
      }

      document.removeEventListener('click', close);
      setIsOpen(false);
    },
    [ref, setIsOpen]
  );

  /** Remove the global click listener */
  useEffect(() => (): void => document.removeEventListener('click', close), [close]);

  /** Toggle the current state (open/closed) */
  const toggleState = (): void => {
    if (!isOpen) {
      document.addEventListener('click', close);
    }
    setIsOpen((state: boolean) => !state);
  };

  return (
    <div
      className={`inline-block relative gap--${gap} layer--${layer}`}
      ref={ref}
      data-testid={testId}
    >
      <button type="button" onClick={toggleState}>
        {trigger}
      </button>
      {isOpen}
      <div
        className={`
          absolute
          bg-white
          mt-1
          p-4
          shadow-lg
          transform
          ${pos.get(position)}
          ${isOpen ? null : 'hidden'}
        `}
      >
        {children}
      </div>
    </div>
  );
};

export default Dropdown;
